import { useEffect, useRef, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { fetch } from "../utils/fetch";
import { FloatingLabel } from "./FloatingLabel";
import { Col, Row } from "./Grid";
import { HiddenValidationControl } from "./HiddenValidationControl";
import Icon from "./Icon";
import PopupContainer from "./PopupContainer";
import TextInput from "./TextInput";
import PropTypes from 'prop-types';

export const customCtrlDef = {
    def: (t, tools) => [{
        borderless: true,
        rows: [
            ...tools.labelFields(t),
            ...tools.fieldFields(t),
            ...tools.commonFields(t)
        ]
    }],
    control: ({ def, href, ...props }) => <IconPicker href={href ?? def.href} {...props}></IconPicker>
}

/**
 * An Fontawesome icon picker
 * @component
 */
const IconPicker = ({ value, onChange, placeholder, defaultIcons = [], hideLabel = false, disabled = false, required = false, readonly = false, href }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [icon, setIcon] = useState(value);
    const [searchTerm, setSearchTerm] = useState();
    const [icons, setIcons] = useState();

    const buttonRef = useRef();
    const [showSearch, setShowSearch] = useState(false);

    useEffect(() => {
        setIcon(value);
    }, [value]);

    useEffect(() => {
        if (searchTerm?.length > 0) {
            setLoading(true);
            // the invoked native function is to ensure linters don't think the void timeout function is returning a promise
            const delayId = setTimeout(() => {
                (async () => {
                    const response = await fetch('https://api.fontawesome.com', {
                        method: 'POST',
                        headers: { 'Content-Type': 'text/plain' },
                        body: `query { search(version: "6.0.0", query: "${searchTerm}", first: 50) { id label familyStylesByLicense { free { style } } } }`
                    })
                    if (response.ok) {
                        const data = await response.json();
                        setIcons(data.data.search.filter(item => item.familyStylesByLicense.free.length > 0).flatMap(item => item.familyStylesByLicense.free.map(s => ({ id: item.id, label: item.label, style: s.style }))));
                    }
                    setLoading(false);
                })()
            }, 500);
            return () => clearTimeout(delayId);
        } else {
            setIcons();
            setLoading(false);
        }
    }, [searchTerm])

    const handleSearch = async text => {
        setLoading(true);
        setSearchTerm(text);
    }

    const handleSelect = iconClass => {
        setIcon(iconClass);
        setShowSearch(false);
        setSearchTerm();
        onChange(iconClass)
    }

    if (readonly) {
        return <Icon literal={icon}></Icon>
    }
    const iconList = searchTerm ? icons : defaultIcons;

    return <FloatingLabel placeholder={placeholder} hideLabel={hideLabel} value={true} required={required} c>
        <div style={{ position: 'relative' }}>
            <Button ref={buttonRef} variant="default" disabled={disabled} onClick={() => setShowSearch(!showSearch)}>
                {icon ? <Icon literal={icon}></Icon> : ' ? '}
            </Button>
            {showSearch && <PopupContainer ctrlRef={buttonRef}>
                <TextInput live={true} value={searchTerm} placeholder={t('search')} onChange={handleSearch} ></TextInput>
                
                    {loading && <div className="text-muted d-flex flex-column align-items-center justify-content-center">
                        <Spinner></Spinner>
                        <div className="text-center">{t('loading')}</div>
                    </div>}
                    {!loading && iconList?.length > 0 && <div className="p-2 mt-2" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gridTemplateRows: 'repeat(5, 1fr)', gap: '10px' }}>
                        {iconList.slice(0, 20).map(i => {
                            return <Button variant="outline-dark" key={i.id} className="w-100 text-center" onClick={() => handleSelect(`fa-${i.style} fa-${i.id}`)}>
                                <Row>
                                    <Col>
                                        <Icon variant={i.style} kind={i.id} style={{ fontSize: '14pt' }}></Icon>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        {i.label}
                                    </Col>
                                </Row>
                            </Button>
                        })}
                    </div>}
                    {!loading && !iconList?.length && <div className="p-2 mt-2 text-muted">{t('emptyIconPicker')}</div>}
            </PopupContainer>}
            <HiddenValidationControl value={value} required={required}></HiddenValidationControl>
        </div>
    </FloatingLabel>;
}

IconPicker.propTypes = {
    /**
     * The currently selected icon
     */
    value: PropTypes.string,
    /**
     * Function to call when the icon has been changed
     */
    onChange: PropTypes.func,
    /**
     * The label for the control
     */
    placeholder: PropTypes.string,
    /**
     * Icons to show on the picker when no search string is selected
     */
    defaultIcons: PropTypes.array,
    /**
     * The control must have a value for the from in which it is contained to validate
     */
    required: PropTypes.bool,
    /**
     * Force the input to be read-only
     */
    disabled: PropTypes.bool,
    /**
     * Render the control value as text rather an input control
     */
    readonly: PropTypes.bool,
    /**
     * Collapses the floating label used for spacing
     */
    hideLabel: PropTypes.bool,

    /**
     * An option navigation URL to be followed if the label text is clicked
     */
    href: PropTypes.string,
}

export default IconPicker;